import React, { useState, useMemo } from 'react';
import { Box, Typography, Avatar, TextField, Card } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { th } from 'date-fns/locale';
import ResponsiveAppBar from '../../../../component/booking_appbar';

// ฟังก์ชันสุ่มสี
const getRandomColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

// MockUp data ที่ปรับปรุงแล้ว
const mockData = [
  {
    id: 1,
    boatName: '240900016',
    bookingId: 'วาริช',
    schedules: [
      {
        total_adults: 4,
        total_children: 1,
        total_people: 5,
        date: '2024-09-24',
        startTime: '10:00',
        endTime: '12:00',
      },
      {
        total_adults: 2,
        total_children: 0,
        total_people: 2,
        date: '2024-09-24',
        startTime: '14:00',
        endTime: '15:45',
      }
    ]
  },
  {
    id: 2,
    boatName: '240900017',
    bookingId: 'สมชาย',
    schedules: [
      {
        total_adults: 2,
        total_children: 2,
        total_people: 4,
        date: '2024-09-24',
        startTime: '10:30',
        endTime: '11:15',
      }
    ]
  }
];

const BoatScheduleTimeline = () => {
  const [selectedDate, setSelectedDate] = useState(new Date('2024-09-24'));
  const timeSlots = Array.from({ length: 37 }, (_, i) => {
    const hour = Math.floor(i / 4) + 9;
    const minute = (i % 4) * 15;
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  });

  const colorMap = useMemo(() => {
    return mockData.reduce((acc, item) => {
      acc[item.id] = getRandomColor();
      return acc;
    }, {});
  }, []);

  const filteredData = mockData.filter(item =>
    item.schedules.some(schedule => new Date(schedule.date).toDateString() === selectedDate.toDateString())
  );

  const calculatePosition = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return (hours - 9) * 4 + minutes / 15;
  };

  return (
    <Box sx={{ 
      width: '150%',
      margin:'10px',
      height: '60vh', // กำหนดความสูงเต็มหน้าจอ
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden' // ป้องกันการเลื่อนเกิน
    }}>
      <Card sx={{ 
        flex: 1, // ให้ Card ขยายเต็มพื้นที่ที่เหลือ
        display: 'flex',
        flexDirection: 'column',
        margin: 2,
        padding:2,
        overflow: 'hidden' // ป้องกันการเลื่อนเกิน
      }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={th}>
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            height: '100%', // ใช้พื้นที่ทั้งหมดของ Card
            p: 3
          }}>
            {/* ส่วนหัวของตาราง */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 2 
            }}>
              <Typography variant="h6">
                ตารางการเดินเรือ
              </Typography>
              <DatePicker
                label="เลือกวันที่"
                value={selectedDate}
                onChange={(newValue) => setSelectedDate(newValue)}
                renderInput={(params) => <TextField {...params} />}
              />
            </Box>

            {/* ส่วนเนื้อหาตาราง */}
            <Box sx={{ 
              flex: 1, // ให้ส่วนตารางขยายเต็มพื้นที่ที่เหลือ
              display: 'flex',
              overflow: 'auto' // อนุญาตให้เลื่อนได้เมื่อเนื้อหาเกิน
            }}>
              {/* คอลัมน์ชื่อเรือ */}
              <Box sx={{ 
                width: 200, 
                flexShrink: 0,
                borderRight: '1px solid #f0f0f0',
                backgroundColor: 'background.paper'
              }}>
                <Typography variant="subtitle2" sx={{ 
                  height: 50, 
                  lineHeight: '50px', 
                  borderBottom: '1px solid #f0f0f0',
                  pl: 2
                }}>
                  ชื่อเรือ
                </Typography>
                {filteredData.map((item, index) => (
                  <Box key={item.id} sx={{ 
                    height: 50, 
                    display: 'flex', 
                    alignItems: 'center', 
                    borderBottom: '1px solid #f0f0f0',
                    pl: 2
                  }}>
                    <Avatar sx={{ 
                      width: 30, 
                      height: 30, 
                      marginRight: 1, 
                      bgcolor: colorMap[item.id] 
                    }}>
                      {index + 1}
                    </Avatar>
                    <Typography variant="body2">{item.bookingId}</Typography>
                  </Box>
                ))}
              </Box>

              {/* ส่วนไทม์ไลน์ */}
              <Box sx={{ 
                flex: 1,
                overflow: 'auto'
              }}>
                <Box sx={{ display: 'flex', borderBottom: '1px solid #f0f0f0' }}>
                  {timeSlots.map((time, index) => (
                    <Box key={time} sx={{ 
                      height: 50, 
                      width: 25, 
                      flexShrink: 0, 
                      textAlign: 'center',
                      backgroundColor: index % 2 === 0 ? 'background.paper' : 'background.default'
                    }}>
                      {index % 4 === 0 && (
                        <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                          {time}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
                {filteredData.map((item) => (
                  <Box key={item.id} sx={{ 
                    height: 50, 
                    position: 'relative', 
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    {item.schedules
                      .filter(schedule => new Date(schedule.date).toDateString() === selectedDate.toDateString())
                      .map((schedule, scheduleIndex) => {
                        const startPosition = calculatePosition(schedule.startTime);
                        const endPosition = calculatePosition(schedule.endTime);
                        const width = (endPosition - startPosition) * 25;

                        return (
                          <Box
                            key={`${item.id}-${scheduleIndex}`}
                            sx={{
                              position: 'absolute',
                              left: startPosition * 25,
                              width: width,
                              height: 30,
                              top: 10,
                              backgroundColor: colorMap[item.id],
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                            }}
                          >
                            <Typography variant="caption" sx={{ 
                              color: 'white', 
                              fontSize: '0.7rem', 
                              whiteSpace: 'nowrap' 
                            }}>
                              {`${schedule.total_people} คน`}
                            </Typography>
                          </Box>
                        );
                      })}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </LocalizationProvider>
      </Card>
    </Box>
  );
};

export default BoatScheduleTimeline;